<script setup lang="ts">
/**
 * The Combobox component provides a combination of input fields and dropdown menus for things like autocomplete search
 * bars, country selection, and filtering options. To use the combobox, bind the `selected` and `query` props. The items
 * have to be filtered outside the combobox. When `emptyTextSelectable` is true and `emptyText`is selected, then
 * `selected` is set to `null`.
 */

export interface Item {
  key: string
  label: string
  title?: string
}

const props = defineProps<{
  items: Item[]
  selected: Item | null
  query: string
  placeholder?: string
  emptyText?: string
  emptyTextSelectable?: boolean
}>()

const emit = defineEmits<{
  (e: "update:query", value: string): void
  (e: "update:selected", value: Item | null): void
}>()

const query = computed({
  get: () => props.query,
  set: (value) => { emit('update:query', value) }
})

const selected = computed({
  get: () => props.selected,
  set: (value) => { emit('update:selected', value) }
})

const cursor = ref<Item | null>(props.items.length == 0 ? null : props.items[0])
watch(() => props.items, (val) => {
  // reset cursor if item no longer exists
  if (!val.find(item => item.key == cursor.value?.key)) {
    cursor.value = val.length == 0 ? null : val[0]
  }
})

function select(item: Item | null) {
  if (item != null) {
    query.value = item.label
  }
  if (item !== null || props.emptyTextSelectable) {
    selected.value = unref(item)
  }
}

function next() {
  if (props.items.length === 0) {
    return
  }
  const idx = props.items.findIndex(item => item.key == cursor.value?.key)
  cursor.value = props.items[(idx + 1) % props.items.length]
}

function previous() {
  if (props.items.length === 0) {
    return
  }
  const idx = props.items.findIndex(item => item.key == cursor.value?.key)
  cursor.value = props.items[(idx - 1 + props.items.length) % props.items.length]
}
</script>

<template>
  <div class="relative w-min">
    <input
      id="combobox-input"
      class="peer/input text-gray-900 rounded focus:outline-none focus:ring-0 border-none"
      @keydown.up.prevent="previous"
      @keydown.down.prevent="next"
      @keydown.enter.prevent="() => select(cursor)"
      v-model="query"
      :placeholder="props.placeholder"
    >
    <div class="absolute right-0 left-0 mt-1 origin-top-right rounded-md bg-secondary-highlighted text-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none hidden peer-focus/input:flex hover:flex flex-col">
      <template v-for="item in props.items" :key="item.key">
        <button
          :aria-selected="item.key == cursor?.key"
          @click="() => select(item)"
          @mouseover="cursor = item"
          class="text-left px-4 py-2 aria-selected:text-primary active:text-primary-selected"
          :title="item.title"
        >
          <slot :item="item" :selected="item.key == cursor?.key">
            {{ item.label }}
          </slot>
        </button>
      </template>

      <button
        v-show="props.emptyText && props.items.length == 0"
        :disabled="!props.emptyTextSelectable"
        @click="() => select(null)"
        class="text-left text-sm font-light px-4 py-2 text-primary active:text-primary-selected
          disabled:text-white disabled:active:text-white"
      >
        {{ props.emptyText }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="postcss">

</style>
